import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const documents = [
  { doc: 'CUET PG 2026 Scorecard & Admit Card', format: 'PDF', purpose: 'Primary eligibility proof' },
  { doc: 'Class 10 Marksheet', format: 'JPG / PDF', purpose: 'Date of birth & school board proof' },
  { doc: 'Class 12 Marksheet', format: 'JPG / PDF', purpose: 'Basic academic eligibility' },
  { doc: "Bachelor's Degree Marksheets (All Semesters)", format: 'PDF', purpose: 'UG qualification proof' },
  { doc: 'Category Certificate (OBC-NCL / SC / ST / EWS)', format: 'PDF', purpose: 'Reservation benefit — if applicable' },
  { doc: 'Transfer & Migration Certificates', format: 'PDF', purpose: 'From previously attended institution' },
  { doc: 'Government Photo ID (Aadhaar / PAN / Voter ID)', format: 'JPG / PDF', purpose: 'Identity verification' },
  { doc: 'Passport-size Photograph (Recent)', format: 'JPG — white background', purpose: 'Application profile & ID card' },
];

const universities = [
  { name: 'University of Delhi (DU)', portal: 'https://admission.uod.ac.in', display: 'admission.uod.ac.in', state: 'Delhi' },
  { name: 'Banaras Hindu University (BHU)', portal: 'https://bhuonline.in', display: 'bhuonline.in', state: 'Uttar Pradesh' },
  { name: 'Jawaharlal Nehru University (JNU)', portal: 'https://jnee.jnu.ac.in', display: 'jnee.jnu.ac.in', state: 'Delhi' },

];

const steps = [
  { title: 'University Registration', desc: 'Navigate to the official website of each university you wish to apply to. Locate the CUET PG 2026 Admissions tab. Do not confuse this with the NTA portal — seat allotment happens at the university, not on ntaexam.org.' },
  { title: 'Login & Data Entry', desc: 'Log in using your CUET PG 2026 Application Number and the password you set during NTA registration. First-time users on a university portal may need to complete a fresh registration using their NTA credentials.' },
  { title: 'Choice Filling (Programme & College Preference)', desc: 'Select your preferred postgraduate courses and, where applicable, campus or college in order of priority. Your allotted seat will be determined by your rank, category, and the preference order you submit here.' },
  { title: 'Document Upload', desc: 'Upload scanned copies of all required certificates in the specified format (JPG/PDF, typically under 200 KB each). Use the checklist below to ensure nothing is missing before submission.' },
  { title: 'Seat Allotment Check', desc: "Monitor the university's official notice board or your registered portal account for merit list announcements. Multiple rounds (Round 1, Round 2, Spot Round) are typically conducted to fill all available seats." },
  { title: 'Admission Fee Payment', desc: 'If allotted a seat, confirm your admission by paying the prescribed fee before the stated deadline. Failure to pay within the window typically results in automatic cancellation and seat reallocation.' },
];

const faqs = [
  {
    q: 'Can I apply to a university I did not select in my NTA application form?',
    a: "Many participating universities allow you to register for counselling even if you did not originally select them in your NTA form — provided you meet their specific subject and eligibility criteria. However, this is not universal. Always check the admissions notification on that university's official portal.",
  },
  {
    q: "What is the 'Spot Round' in CUET PG counselling?",
    a: 'A Spot Round is an additional admission round conducted after all primary rounds have been completed, used to fill seats that remain vacant. Spot rounds often have a compressed timeline — sometimes 24–48 hours — so check university portals frequently near the end of the regular counselling window.',
  },
  {
    q: 'Is the CUET PG 2026 counselling process centralised or decentralised?',
    a: 'It is decentralised. NTA conducts the CUET PG examination and releases scores, but each participating university independently manages its own merit list, counselling schedule, seat allotment, and fee payment. There is no single national counselling portal for CUET PG — unlike JEE Main (JoSAA) or NEET PG.',
  },
  {
    q: 'How is the merit list prepared for CUET PG 2026?',
    a: 'Each university prepares its own merit list based on CUET PG scores of candidates registered on their portal. The merit list ranks candidates by their score in the relevant subject paper, category (General, OBC-NCL, SC, ST, EWS), and seats available in each programme.',
  },
  {
    q: 'What happens if I miss the fee payment deadline after seat allotment?',
    a: 'Missing the fee payment deadline after a seat has been allotted typically results in automatic cancellation of your seat. The vacated seat is then reallocated in the next round. Most universities do not offer grace periods, so set calendar reminders as soon as you receive an allotment.',
  },
];

const CuetPgCounsellingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      <Helmet>
        <title>CUET PG Counselling 2026: Step-by-Step Guide, Dates & University Portal Links</title>
        <meta name="description" content="Navigating CUET PG 2026 counselling? Get the latest schedule, required document checklist, and direct portal links for DU, BHU, JNU, and more. Secure your PG seat today." />
        <meta name="keywords" content="CUET PG counselling 2026, CUET PG 2026 counselling process, CUET PG seat allotment, CUET PG university portals, CUET PG document checklist" />
        <link rel="canonical" href="https://besst.in/cuet-pg-counselling" />
        <meta property="og:title" content="CUET PG Counselling 2026: Step-by-Step Guide, Dates & University Portal Links" />
        <meta property="og:description" content="Navigating CUET PG 2026 counselling? Get the latest schedule, required document checklist, and direct portal links for DU, BHU, JNU, and more." />
        <meta property="og:url" content="https://besst.in/cuet-pg-counselling" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="BESST" />
        {/* Article schema */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "CUET PG Counselling 2026: Step-by-Step Guide, Dates & University Portal Links",
          "description": "Navigating CUET PG 2026 counselling? Get the latest schedule, required document checklist, and direct portal links for DU, BHU, JNU, and more.",
          "author": { "@type": "Organization", "name": "Besst.in Editorial Team" },
          "publisher": { "@type": "Organization", "name": "BESST", "url": "https://besst.in" },
          "dateModified": "2026-05-01",
          "url": "https://besst.in/cuet-pg-counselling"
        })}</script>
        {/* FAQ schema */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      <div className="min-h-screen pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">

          {/* ── Breadcrumb ── */}
          <div className="flex items-center gap-2 text-text-muted text-sm mb-8">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span>/</span>
            <Link to="/cuet-counselling" className="hover:text-primary transition-colors">CUET Counselling</Link>
            <span>/</span>
            <span className="text-text-light">CUET PG 2026</span>
          </div>

          {/* ── Hero ── */}
          <div className="mb-10">
            <div className="hero-badge mb-4">🎓 CUET PG 2026</div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold gradient-text mb-4 leading-tight">
              CUET PG Counselling 2026
            </h1>
            <p className="text-xl text-text-muted mb-4">
              Step-by-Step Guide, Dates &amp; University Portal Links
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-text-muted">
              <span>✍️ By Besst.in Editorial Team</span>
              <span>📅 Last Updated: May 2026</span>
              <span>⏱️ ~6 min read</span>
            </div>
          </div>

          {/* ── Quick Overview ── */}
          <div className="glass-card mb-10 border-l-4 border-accent-gold">
            <h2 className="text-xl font-bold text-accent-gold mb-4">⚡ Quick Overview: CUET PG 2026 Counselling Status</h2>
            <p className="text-text-light leading-relaxed mb-4">
              CUET PG 2026 results have been released by NTA. Individual universities — including DU, BHU, and JNU — are now opening their separate counselling portals. You must register on each university's official website individually; there is no centralised counselling window.
            </p>
            <p className="text-text-muted text-sm leading-relaxed">
              While NTA administers the CUET PG exam and releases raw scores, the responsibility for generating merit lists, managing seat allotment rounds, and finalising admissions rests entirely with each participating university. Timelines, cut-offs, and document requirements vary across institutions.
            </p>
            <div className="mt-4 p-4 bg-primary/10 border border-primary/30 rounded-xl">
              <p className="text-primary text-sm font-semibold mb-1">💡 Expert Note from Besst.in</p>
              <p className="text-text-muted text-sm">Our academic counselling team has guided 10,000+ students through CUET admissions since the exam's inception. The single biggest mistake we see is candidates waiting for a centralised notification. <strong className="text-text-light">Start your university-wise registrations immediately</strong> — seat availability reduces significantly after Round 1.</p>
            </div>
          </div>

          {/* ── Step-by-Step Process ── */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold gradient-text mb-6">Step-by-Step CUET PG 2026 Counselling Process</h2>
            <p className="text-text-muted text-sm mb-6">The following process applies to most participating central universities. Always verify the exact sequence on your target university's official portal before proceeding.</p>
            <div className="space-y-4">
              {steps.map((step, i) => (
                <div key={i} className="glass-card flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center font-bold text-primary text-sm">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="text-text-light font-bold mb-1">Step {i + 1} — {step.title}</h3>
                    <p className="text-text-muted text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Document Checklist ── */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold gradient-text mb-2">Mandatory Document Checklist</h2>
            <p className="text-text-muted text-sm mb-6">Prepare these documents in advance. Most universities require uploads in JPG or PDF format, with individual file sizes not exceeding 200 KB. Submitting incomplete documents is one of the leading causes of admission cancellation.</p>
            <div className="glass-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-glass-border">
                    <th className="text-left py-3 pr-4 text-text-light font-bold">Document</th>
                    <th className="text-left py-3 pr-4 text-text-light font-bold whitespace-nowrap">Format</th>
                    <th className="text-left py-3 text-text-light font-bold">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((d, i) => (
                    <tr key={i} className="border-b border-glass-border/50 hover:bg-white/5 transition-colors">
                      <td className="py-3 pr-4 text-text-light">{d.doc}</td>
                      <td className="py-3 pr-4 text-primary whitespace-nowrap font-mono text-xs">{d.format}</td>
                      <td className="py-3 text-text-muted">{d.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── University Portals ── */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold gradient-text mb-2">Direct Links to Top University Counselling Portals</h2>
            <p className="text-text-muted text-sm mb-6">Bookmark these portals and check them daily for new announcements during the counselling window.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {universities.map((u, i) => (
                <a key={i} href={u.portal} target="_blank" rel="noreferrer noopener"
                  className="glass-card hover:border-primary/50 transition-colors group">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-text-light font-bold mb-1 group-hover:text-primary transition-colors">{u.name}</h3>
                      <p className="text-primary text-xs font-mono">{u.display}</p>
                      <p className="text-text-muted text-xs mt-1">{u.state}</p>
                    </div>
                    <span className="text-primary text-lg flex-shrink-0">→</span>
                  </div>
                </a>
              ))}
            </div>
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
              <p className="text-red-400 text-sm">⚠️ <strong>Important:</strong> Always verify that you are on the official <code className="bg-red-500/20 px-1 rounded">.ac.in</code> or <code className="bg-red-500/20 px-1 rounded">.edu.in</code> domain before entering personal or payment details. Fraudulent counselling sites exist during this period.</p>
            </div>
          </div>

          {/* ── FAQ ── */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold gradient-text mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <div key={i} className="glass-card">
                  <button
                    className="w-full text-left flex justify-between items-start gap-4"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-semibold text-text-light text-sm leading-snug">{f.q}</span>
                    <span className={`text-primary text-xl flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                  </button>
                  {openFaq === i && (
                    <p className="text-text-muted text-sm mt-3 leading-relaxed border-t border-glass-border pt-3">{f.a}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ── CTA — Prep for 2027 ── */}
          <div className="glass-card text-center border-primary/30 mb-10">
            <div className="text-4xl mb-4">📚</div>
            <h2 className="text-2xl font-bold text-text-light mb-3">Didn't Get the Seat You Wanted?</h2>
            <h3 className="text-lg text-accent-gold font-semibold mb-3">Plan Ahead for CUET PG 2027</h3>
            <p className="text-text-muted text-sm mb-6 max-w-lg mx-auto">
              Thousands of CUET aspirants improve their scores significantly in the second attempt with the right structured preparation. Our Self-Paced CUET PG Prep Modules are designed to build domain knowledge, improve speed, and develop the exam temperament you need.
            </p>
            <Link to="/self-paced" className="btn-primary px-8 py-3 font-bold rounded-xl inline-block">
              Explore CUET PG Prep Modules →
            </Link>
          </div>

          {/* ── Author / Disclaimer ── */}
          <div className="glass-card bg-bg-secondary/30 text-sm">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold flex-shrink-0">B</div>
              <div>
                <p className="text-text-light font-semibold">Besst.in Editorial Team</p>
                <p className="text-text-muted text-xs">Experienced academic counsellors, former NTA exam coordinators, and PG admissions specialists who have collectively guided over 10,000 students through CUET PG and other central university admissions.</p>
              </div>
            </div>
            <p className="text-text-muted text-xs border-t border-glass-border pt-4">
              <strong className="text-text-light">Disclaimer:</strong> All information in this article is sourced from NTA's official communications and individual university notifications current as of May 2026. Dates and procedures are subject to change. Always verify details on the official university portals listed above before taking any action. Besst.in is not affiliated with NTA or any participating university.
            </p>
          </div>

        </div>
      </div>
    </>
  );
};

export default CuetPgCounsellingPage;